import type { ComponentConfig } from '@puckeditor/core'
import PaddingField, { type PaddingValue, defaultPadding, paddingToStyle } from './PaddingField'

export interface VideoEmbedProps {
    url: string
    aspectRatio: '16:9' | '4:3' | '1:1'
    borderRadius: number
    maxWidth: string
    alignment: 'left' | 'center' | 'right'
    padding: PaddingValue
}

function getEmbedUrl(url: string): string | null {
    try {
        const parsed = new URL(url)
        if (parsed.hostname.includes('youtube.com') || parsed.hostname.includes('youtu.be')) {
            const videoId = parsed.hostname.includes('youtu.be')
                ? parsed.pathname.slice(1)
                : parsed.searchParams.get('v')
            if (videoId) return `https://www.youtube.com/embed/${videoId}`
        }
        if (parsed.hostname.includes('vimeo.com')) {
            const videoId = parsed.pathname.split('/').filter(Boolean).pop()
            if (videoId) return `https://player.vimeo.com/video/${videoId}`
        }
    } catch {
        // invalid URL
    }
    return null
}

const aspectRatioMap: Record<string, string> = {
    '16:9': '56.25%',
    '4:3': '75%',
    '1:1': '100%',
}

export const VideoEmbedBlock: ComponentConfig<VideoEmbedProps> = {
    label: 'Video',
    defaultProps: {
        url: '',
        aspectRatio: '16:9',
        borderRadius: 8,
        maxWidth: '100%',
        alignment: 'center',
        padding: defaultPadding,
    },
    fields: {
        url: { type: 'text', label: 'Video URL (YouTube or Vimeo)' },
        aspectRatio: {
            type: 'radio',
            label: 'Aspect Ratio',
            options: [
                { label: '16:9', value: '16:9' },
                { label: '4:3', value: '4:3' },
                { label: '1:1', value: '1:1' },
            ],
        },
        borderRadius: { type: 'number', label: 'Border Radius', min: 0, max: 48 },
        maxWidth: { type: 'text', label: 'Max Width (e.g., 100%, 500px)' },
        alignment: {
            type: 'radio',
            label: 'Alignment',
            options: [
                { label: 'Left', value: 'left' },
                { label: 'Center', value: 'center' },
                { label: 'Right', value: 'right' },
            ],
        },
        padding: {
            type: 'custom',
            render: ({ value, onChange }) => <PaddingField value={value} onChange={onChange} />,
        },
    },
    render: ({ url, aspectRatio, borderRadius, maxWidth, alignment, padding }) => {
        const embedUrl = url ? getEmbedUrl(url) : null

        if (!embedUrl) {
            return (
                <div style={{
                    textAlign: alignment,
                    padding: paddingToStyle(padding),
                    border: '2px dashed #d1d5db',
                    borderRadius: 8,
                    color: '#9ca3af',
                    fontSize: 14,
                }}>
                    {url ? 'Invalid video URL. Use a YouTube or Vimeo link.' : 'Enter a video URL'}
                </div>
            )
        }

        return (
            <div style={{ textAlign: alignment, padding: paddingToStyle(padding) }}>
                <div style={{
                    position: 'relative',
                    paddingBottom: aspectRatioMap[aspectRatio],
                    height: 0,
                    overflow: 'hidden',
                    maxWidth,
                    borderRadius,
                    display: 'inline-block',
                    width: '100%',
                }}>
                    <iframe
                        src={embedUrl}
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            border: 'none',
                            borderRadius,
                        }}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                    />
                </div>
            </div>
        )
    },
}
